import { AnimatedNumber } from './AnimatedNumber';
import { useCallback, useEffect, useState } from 'react';
import { ArrowUpRight, Check, Clock3, ShieldCheck, X } from 'lucide-react';
import { logoSources } from '@pons/contracts/logoSources';
import { isUsdReady } from './market';

import type { Choice, Round } from '@pons/contracts';
export type { Choice, Round } from '@pons/contracts';
export const isOpen = (r: Round, now: number) =>
  !r.outcome &&
  r.canonical !== false &&
  isUsdReady(r, now) &&
  (r.ruleVersion !== 'usd-v2' || Number(r.p0Usd) > 0) &&
  now >= r.start &&
  now < r.predictionClosesAt;
export const isWatching = (r: Round, now: number) =>
  !r.outcome && now >= r.predictionClosesAt && now < r.settlesAt;
const waitingForOpening = (r: Round, now: number) =>
  !r.outcome &&
  r.canonical !== false &&
  now >= r.start &&
  now < r.predictionClosesAt &&
  !isOpen(r, now);
export function duration(ms: number) {
  const s = Math.max(0, Math.ceil(ms / 1000));
  return `${Math.floor(s / 60)
    .toString()
    .padStart(2, '0')}:${(s % 60).toString().padStart(2, '0')}`;
}
export function ago(time: number, now: number) {
  const s = Math.max(0, Math.floor((now - time) / 1000));
  return s < 60
    ? s + 's ago'
    : s < 3600
      ? Math.floor(s / 60) + 'm ago'
      : s < 86400
        ? Math.floor(s / 3600) + 'h ago'
        : Math.floor(s / 86400) + 'd ago';
}
export function change(r: Round) {
  return r.currentPriceUsd != null && r.p0Usd != null && Number(r.p0Usd) > 0
    ? (Number(r.currentPriceUsd) / Number(r.p0Usd) - 1) * 100
    : null;
}
export function usd(value: string | null | undefined) {
  if (value == null || !Number.isFinite(Number(value))) return '—';
  return '$' + number(Number(value));
}
export function number(n: number | null) {
  if (n === null || !Number.isFinite(n)) return '—';
  if (n > 0 && n < 0.0001) return '<0.0001';
  return n.toLocaleString(
    'en-US',
    n >= 10000
      ? { notation: 'compact', maximumFractionDigits: 2 }
      : { maximumFractionDigits: n >= 1 ? 2 : 4 },
  );
}
const percent = (n: number) => (n >= 0 ? '+' : '') + n.toFixed(1) + '%';
export function Art({
  symbol,
  r,
  className = '',
}: {
  symbol: string;
  r?: Round;
  className?: string;
}) {
  return (
    <TokenLogo
      key={(r?.address || '') + ':' + (r?.logo || 'missing')}
      address={r?.address}
      source={r?.logo || null}
      symbol={symbol}
      className={className}
    />
  );
}
function TokenLogo({
  address,
  source,
  symbol,
  className,
}: {
  address?: string;
  source: string | null;
  symbol: string;
  className: string;
}) {
  let version = 0;
  for (const char of source || '')
    version = (Math.imul(version, 31) + char.charCodeAt(0)) >>> 0;
  const primary =
    address && source
      ? '/api/token-logo/' + address + '?v=' + version.toString(36)
      : null;
  const ipfs = logoSources(source).length > 1;
  const candidates = [
    ...(primary ? [primary] : []),
    ...(!ipfs && source ? [source] : []),
  ];
  const [attempt, setAttempt] = useState(0),
    [retry, setRetry] = useState(0),
    [failed, setFailed] = useState(false),
    [loaded, setLoaded] = useState(false);
  const candidate = candidates[attempt];
  const src = candidate?.startsWith('/api/')
    ? candidate + '&attempt=' + retry
    : candidate;
  const onError = useCallback(() => {
    setLoaded(false);
    if (attempt + 1 < candidates.length) setAttempt(attempt + 1);
    else setFailed(true);
  }, [attempt, candidates.length]);
  useEffect(() => {
    if (!failed || retry >= 3 || !source) return;
    const timer = setTimeout(() => {
      setAttempt(0);
      setRetry((v) => v + 1);
      setLoaded(false);
      setFailed(false);
    }, [6000, 20000, 60000][retry]);
    return () => clearTimeout(timer);
  }, [failed, retry, source]);
  useEffect(() => {
    if (!src || failed || loaded) return;
    const timer = setTimeout(onError, 15000);
    return () => clearTimeout(timer);
  }, [src, failed, loaded, onError]);
  return (
    <div
      role="img"
      aria-label={symbol + ' token logo'}
      className={'token-art live-art ' + className}
    >
      {!loaded && (
        <span
          className="logo-placeholder"
          title={source ? 'Loading token logo' : 'No logo available'}
        >
          {symbol.slice(0, 2).toUpperCase()}
        </span>
      )}
      {src && !failed && (
        <img
          key={retry + ':' + src}
          src={src}
          alt=""
          onError={onError}
          onLoad={() => setLoaded(true)}
          referrerPolicy="no-referrer"
          loading="eager"
          decoding="async"
          style={{ opacity: loaded ? 1 : 0 }}
        />
      )}
    </div>
  );
}
function Spark({ r }: { r: Round }) {
  const values = (r.historyUsd || []).map(Number).filter(Number.isFinite);
  if (values.length < 2) return null;
  const lo = Math.min(...values),
    hi = Math.max(...values);
  const points = values
    .map(
      (v, i) =>
        (i / (values.length - 1)) * 120 +
        ',' +
        (hi === lo ? 25 : 45 - ((v - lo) / (hi - lo)) * 40),
    )
    .join(' ');
  return (
    <svg
      className={
        'spark ' + (values.at(-1)! >= values[0] ? 'positive' : 'negative')
      }
      viewBox="0 0 120 50"
      aria-label="Recent observed price trend in USD"
      role="img"
    >
      <polyline points={points} />
    </svg>
  );
}
export function Status({ r, now }: { r: Round; now: number }) {
  return (
    <span
      className={
        'round-status ' +
        (r.outcome === 'SURVIVES'
          ? 'success'
          : r.outcome === 'DIES'
            ? 'danger'
            : waitingForOpening(r, now)
              ? 'pending'
              : isOpen(r, now)
                ? 'open'
                : now >= r.settlesAt
                  ? 'pending'
                  : 'watching')
      }
    >
      <i />
      {r.canonical === false
        ? 'Rechecking chain data'
        : r.outcome
          ? r.outcome === 'SURVIVES'
            ? 'Survived'
            : 'Did not survive'
          : waitingForOpening(r, now)
            ? 'Verifying opening price'
            : isOpen(r, now)
              ? 'Open now'
              : now >= r.settlesAt
                ? 'Verifying result'
                : 'Watching'}
    </span>
  );
}
export function Actions({
  r,
  now,
  onPredict,
}: {
  r: Round;
  now: number;
  onPredict: (id: string, c: Choice) => void;
}) {
  if (r.choice)
    return (
      <div
        className={
          'locked-call ' + (r.choice === 'SURVIVES' ? 'positive' : 'negative')
        }
      >
        <ShieldCheck size={15} />
        <span>
          Your call: <b>{r.choice === 'SURVIVES' ? 'Survives' : 'Dies'}</b>
        </span>
        <Check size={14} />
      </div>
    );
  if (!isOpen(r, now)) return null;
  return (
    <div className="choices">
      {(['SURVIVES', 'DIES'] as Choice[]).map((c) => (
        <button
          key={c}
          className={c === 'SURVIVES' ? 'survives' : 'dies'}
          onClick={() => onPredict(r.id, c)}
        >
          {c === 'SURVIVES' ? <ArrowUpRight size={16} /> : <X size={16} />}{' '}
          {c === 'SURVIVES' ? 'Survives' : 'Dies'}
        </button>
      ))}
    </div>
  );
}
export function Card({
  r,
  now,
  onPredict,
}: {
  r: Round;
  now: number;
  onPredict: (id: string, c: Choice) => void;
}) {
  const open = isOpen(r, now),
    waiting = waitingForOpening(r, now),
    watching = isWatching(r, now),
    done = !!r.outcome,
    pct = change(r);
  const stale = !isUsdReady(r, now) || !!r.priceError;
  const resultPct =
    r.ptUsd != null && r.p0Usd != null && Number(r.p0Usd) > 0
      ? (Number(r.ptUsd) / Number(r.p0Usd) - 1) * 100
      : null;
  const progress =
    open || waiting
      ? (r.predictionClosesAt - now) / 20000
      : (now - r.start) / (r.settlesAt - r.start);
  return (
    <article
      className={
        'token-card live-card ' +
        (open ? 'is-open ' : '') +
        (done ? 'is-settled' : '')
      }
    >
      <div className="card-topline">
        <Status r={r} now={now} />
        <span className="graduation-age">
          <AnimatedNumber value={ago(r.start, now)} />
        </span>
      </div>
      <div className="coin-identity">
        <Art r={r} symbol={r.symbol} />
        <div>
          <h3 title={r.name}>{r.name}</h3>
          <span title={'$' + r.symbol}>${r.symbol}</span>
        </div>
        <nav className="coin-links" aria-label={r.name + ' trading platforms'}>
          <a
            className="coin-link"
            href={'https://www.ponsfamily.com/launchpad/' + r.address}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={'View ' + r.name + ' on Pons (opens in a new tab)'}
          >
            Pons <ArrowUpRight size={12} aria-hidden="true" />
          </a>
          <a
            className="coin-link"
            href={'https://fomo.family/tokens/robinhood/' + r.address}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={'View ' + r.name + ' on Fomo (opens in a new tab)'}
          >
            Fomo <ArrowUpRight size={12} aria-hidden="true" />
          </a>
        </nav>
      </div>
      <div className="quote-section">
        <div className="quote-label">
          <span title="USD token price × total token supply. This is a fully diluted valuation, not verified circulating supply.">
            Market cap <small>· USD</small>
          </span>
          {stale && <span className="stale-tag">Last known</span>}
        </div>
        <div className="quote-value">
          {r.currentMCUsd == null ? (
            <span className="quote-loading">
              Updating<span>•••</span>
            </span>
          ) : (
            <b title={'$' + r.currentMCUsd}>
              <AnimatedNumber value={usd(r.currentMCUsd)} />
            </b>
          )}
          <Spark r={r} />
        </div>
        <div className="quote-change">
          {pct !== null ? (
            <>
              <span className={pct >= 0 ? 'positive' : 'negative'}>
                <AnimatedNumber value={percent(pct)} />
              </span>
              <span>since graduation</span>
            </>
          ) : (
            <span>Historical USD baseline unavailable</span>
          )}
        </div>
      </div>
      <div className="round-facts">
        <div>
          <span>At graduation</span>
          <b>
            <AnimatedNumber value={usd(r.capUsd)} />
          </b>
        </div>
        <div>
          <span>Trading pair</span>
          {r.pairToken === '0x0000000000000000000000000000000000000000' ? (
            <b>ETH</b>
          ) : (
            <a
              href={
                'https://robinhoodchain.blockscout.com/address/' + r.pairToken
              }
              target="_blank"
              rel="noreferrer"
            >
              {r.quoteSymbol || 'View token'} <ArrowUpRight size={11} />
            </a>
          )}
        </div>
      </div>
      <div className={'round-phase ' + (done ? 'phase-settled' : '')}>
        <div>
          <span>
            {done ? <ShieldCheck size={14} /> : <Clock3 size={14} />}{' '}
            {done
              ? r.ruleVersion === 'usd-v2'
                ? 'Final at 20 min · USD'
                : 'Final · original pair rules'
              : waiting
                ? 'Opening price pending'
                : open
                  ? 'Prediction closes in'
                  : watching
                    ? 'Result in'
                    : 'Checking price & trades'}
          </span>
          <b>
            <AnimatedNumber
              value={
                done
                  ? resultPct !== null
                    ? percent(resultPct)
                    : 'Settled'
                  : open || waiting
                    ? duration(r.predictionClosesAt - now)
                    : watching
                      ? duration(r.settlesAt - now)
                      : 'Pending'
              }
            />
          </b>
        </div>
        {!done && now < r.settlesAt && (
          <div className={'phase-track ' + (open ? 'counting-down' : '')}>
            <i
              style={{
                width: Math.min(100, Math.max(0, progress * 100)) + '%',
              }}
            />
          </div>
        )}
      </div>
      <div className="call-area">
        <Actions r={r} now={now} onPredict={onPredict} />
        {r.choice && done && (
          <p
            className={
              'call-result ' + (r.choice === r.outcome ? 'positive' : '')
            }
          >
            {r.choice === r.outcome
              ? 'Correct call · +100 points'
              : 'Not this time · 0 points'}
          </p>
        )}
        {r.choice ? (
          <div className="community">
            <div>
              <span>Community</span>
              <b>
                <AnimatedNumber
                  value={
                    r.split === null
                      ? 'Waiting for votes'
                      : r.split + '% survives / ' + (100 - r.split) + '% dies'
                  }
                />
              </b>
            </div>
            {r.split !== null && (
              <div className="split">
                <i style={{ width: r.split + '%' }} />
              </div>
            )}
          </div>
        ) : (
          <p className="card-foot">
            {open
              ? 'Make your call to reveal the community split.'
              : waiting
                ? 'Verifying the opening USD price. The original deadline still applies.'
                : done
                  ? 'Round complete. Catch the next graduation.'
                  : 'Prediction window closed'}
          </p>
        )}
      </div>
      <div className="card-data-note">
        <span>
          <AnimatedNumber
            value={
              r.lastTradeAt
                ? 'Last trade ' + ago(r.lastTradeAt, now)
                : 'No observed trades yet'
            }
          />
        </span>
        <span title={r.address}>
          {r.address.slice(0, 6)}…{r.address.slice(-4)}
        </span>
      </div>
      <div className="card-data-note">
        <span title={'USD source: ' + (r.usdSource || 'unavailable')}>
          <AnimatedNumber
            value={
              r.usdStatus === 'halted'
                ? 'USD source paused'
                : r.usdAsOf
                  ? 'USD reference ' + ago(r.usdAsOf, now)
                  : 'USD reference unavailable'
            }
          />
        </span>
        <span title={r.currentPriceUsd ? '$' + r.currentPriceUsd : undefined}>
          Price <AnimatedNumber value={usd(r.currentPriceUsd)} />
        </span>
      </div>
    </article>
  );
}
